'use client';

import { defineMessages, useIntl } from 'react-intl';

import { Chip } from '@packages/shared/src/ui/Chip/Chip';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SelectedFilterValueListFragmentFragmentDoc } from '@packages/gql/generated/shopping/SelectedFilterValueListFragmentFragmentDoc';

import { SPECIAL_CATEGORY_FILTER_ID } from '../../hooks/useSpecialCategoryFilterValue';
import { useSearchFilters } from '../../hooks/useFilters';
import { SelectedFilterValueChip } from '../SelectedFilterValueChip/SelectedFilterValueChip';
import { SelectedFilterRangeChip } from '../SelectedFilterRangeChip/SelectedFilterRangeChip';

const messages = defineMessages({
  deleteAllLabel: {
    id: 'filters.selectedValues.deleteAll',
    defaultMessage: 'Alle Filter zurücksetzen',
  },
  chipsContainerLabel: {
    id: 'filters.chips.container.label',
    defaultMessage: 'Ausgewählte Filter',
  },
});

/* GraphQL */ `
  fragment SelectedFilterValueListFragment on SearchFilterableAndSortableResult {
    ...UseSearchFiltersFragment
    activeFilters {
      filterId
      activeValues {
        id
        ...SelectedFilterValueChipFragment
      }
    }

    ... on SuccessfulSearchResult {
      selectedCategory {
        id
        displayName
      }
    }
  }
`;

export type SelectedFilterValueListProps = {
  maskedData: FragmentType<typeof SelectedFilterValueListFragmentFragmentDoc>;
  /** If `true`, the color of the filter value is displayed on the chip */
  showColors?: boolean;
};

/**
 * A list of currently selected filter values
 * */
export const SelectedFilterValueList = ({
  maskedData,
  showColors,
}: SelectedFilterValueListProps) => {
  const { formatMessage } = useIntl();

  const data = unmask<typeof SelectedFilterValueListFragmentFragmentDoc>(maskedData);

  const { selectedFilterValues, pushFilterChange } = useSearchFilters(data);

  const removeFilterValue = (filterId: string, valueId: string) => {
    const otherValues = selectedFilterValues.filter((value) => value.filterId !== filterId);
    const relevantValue = selectedFilterValues.find((value) => value.filterId === filterId);

    if (!relevantValue) return;

    const { selectedValues, selectedRange } = relevantValue;

    pushFilterChange(
      otherValues.concat({
        filterId,
        selectedRange,
        selectedValues: selectedValues.filter((id) => id !== valueId),
      }),
    );
  };

  const removeRangeValue = (filterId: string) => {
    const otherValues = selectedFilterValues.filter((value) => value.filterId !== filterId);
    const relevantValue = selectedFilterValues.find((value) => value.filterId === filterId);

    if (!relevantValue) return;

    const { selectedValues } = relevantValue;

    pushFilterChange(otherValues.concat({ filterId, selectedValues }));
  };

  const removeAll = () => {
    pushFilterChange([]);
  };

  const getValueById = (filterId: string, valueId: string) =>
    data.activeFilters
      .find((f) => f.filterId === filterId)
      ?.activeValues?.find((v) => v.id === valueId);

  return (
    <div className="mt-1 flex w-full flex-col flex-wrap gap-1 md:flex-row">
      <ul
        className="mr-auto flex w-full flex-1 flex-wrap gap-1"
        aria-label={formatMessage(messages.chipsContainerLabel)}
      >
        {'selectedCategory' in data && data.selectedCategory && (
          <li>
            <Chip
              onDelete={
                () => removeFilterValue(SPECIAL_CATEGORY_FILTER_ID, data.selectedCategory?.id ?? '') // TS can't correctly infer here that `selectedCategory` is defined
              }
              onClick={
                () => removeFilterValue(SPECIAL_CATEGORY_FILTER_ID, data.selectedCategory?.id ?? '') // TS can't correctly infer here that `selectedCategory` is defined
              }
            >
              {data.selectedCategory.displayName}
            </Chip>
          </li>
        )}
        {selectedFilterValues.flatMap(({ filterId, selectedValues, selectedRange }) => (
          <>
            {selectedRange && (
              <li key={`${filterId}-range`}>
                <SelectedFilterRangeChip
                  value={selectedRange}
                  onDelete={() => removeRangeValue(filterId)}
                />
              </li>
            )}
            {selectedValues
              .map((id) => getValueById(filterId, id))
              .map((value) =>
                value ? (
                  <li key={`${filterId}-${value.id}`}>
                    <SelectedFilterValueChip
                      key={`${filterId}-${value.id}`}
                      onDelete={() => removeFilterValue(filterId, value.id)}
                      maskedData={value}
                      showColors={showColors}
                    />
                  </li>
                ) : null,
              )}
          </>
        ))}
      </ul>
      {/* div wrapper provides left-alignment for the Chip on small device sizes (overrides the default stretch alignment of the parent); this is functionally equivalent to `align-self: flex-start` directly on the chip, but makes it consistent with the other chips above that also have a wrapping element */}
      {selectedFilterValues.length > 0 && (
        <div>
          <Chip
            label={formatMessage(messages.deleteAllLabel)}
            onClick={removeAll}
            iconClassName="icon-emp-clear-input"
            iconPosition="end"
          />
        </div>
      )}
    </div>
  );
};
